import React, { useState } from 'react';
import { App, Switch, Typography } from 'antd';

import Guidance from '../common/Guidance';
import { getShareLinks, linkKind, setIntakeLinksAllowed } from '../../services/shareLinks';
import { INTAKE_KIND } from '../../utils/intakeLinks';
import { confirmDestructive } from '../common/confirmDestructive';

const { Text } = Typography;

/**
 * The collection's intake switch: may this collection hand out intake links —
 * public forms that let someone WITHOUT an account send it an item?
 *
 * It is an authorization field with its own door
 * (`PUT /api/collections/{id}/intake`, manage + a verified session), never
 * part of the settings Save: it applies the moment it is flipped, like the
 * rest of this tab. Off stops every live intake link at once (a visitor gets
 * the "link doesn't exist" page); on again brings back the ones that have
 * neither expired nor been revoked.
 *
 * The Sharing tab is manager-only, so everyone who sees this can move it.
 *
 * Turning it OFF while intake links are live asks first, with the count: it
 * is the one flip on this tab that breaks links other people are holding.
 */
export default function IntakeFormsSetting({ collectionId, allowed = false, onChange }) {
	const { message, modal } = App.useApp();
	const [saving, setSaving] = useState(false);

	const apply = async (checked) => {
		setSaving(true);
		try {
			const result = await setIntakeLinksAllowed(collectionId, checked);
			const next = result?.allowIntakeLinks === true;
			onChange?.(next);
			message.success(next
				? 'Intake forms are on. Make one under Share links.'
				: 'Intake forms are off. Every intake link for this collection has stopped working.');
		} catch (error) {
			message.error(error?.response?.data?.message || 'Could not change intake forms.');
		} finally {
			setSaving(false);
		}
	};

	const handleToggle = async (checked) => {
		if (checked) { await apply(true); return; }
		// Counted fresh, not from the panel below: the list may be stale,
		// and the question is how many work right now.
		setSaving(true);
		let live = 0;
		try {
			const links = await getShareLinks(collectionId);
			live = links.filter(link => linkKind(link) === INTAKE_KIND && link?.status === 'active').length;
		} catch {
			live = 0; // the switch still works; the count is a courtesy
		} finally {
			setSaving(false);
		}
		if (!live) { await apply(false); return; }
		confirmDestructive(modal, {
			title: 'Turn off intake forms?',
			content: `${live === 1 ? '1 live intake link' : `${live} live intake links`} will stop working until you turn this back on.`,
			okText: 'Turn off',
			onOk: () => apply(false),
		});
	};

	return (
		<section className="sharing-tab__section intake-forms" aria-labelledby="intake-forms-title">
			<div className="intake-forms__head">
				<h3 id="intake-forms-title">Intake forms</h3>
				<Switch
					checked={allowed}
					loading={saving}
					onChange={handleToggle}
					aria-labelledby="intake-forms-title"
					data-testid="intake-forms-switch"
				/>
			</div>
			<Text type="secondary">
				Let people without an account send this collection items through a form link. What they
				send waits for review before it joins the list.
			</Text>
			{allowed ? (
				<Guidance
					className="intake-forms__guidance"
					title="Anyone holding an intake link can write to this collection"
					why="Give each link one card, only the fields you need, and a short expiry. Review what arrives from the collection's home — nothing counts, exports or shows publicly until you accept it."
					links={['sharing-and-export/collect-items-with-an-intake-form', 'items/review-intake-submissions']}
					tone="caution"
					dismissKey="intake-forms-on"
				/>
			) : null}
		</section>
	);
}
